import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Container,
    ContainerLoadingMore,
    ContentButtons, ContentLabel,
    ContentSelect,
    Label, SelectDropdownGlobalStyle,
    SelectStyled,
    TextLoadingMore
} from "./styled";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {Brightness} from "@dropDesk/domain/entities/theme/colors.entity";
import {SharedColors} from "@dropDesk/domain/entities/theme/app_colors";
import LabelInput from "@dropDesk/presentation/components/inputs/input_with_border/label";
import LeftIcon from "@dropDesk/presentation/components/inputs/input_with_border/left_icon";
import MessageError from "@dropDesk/presentation/components/inputs/input_with_border/message_error";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ConstantsKeys, ConstantsStyles} from "@dropDesk/domain/entities/constants/constants_keys";
import {useDebouncedCallback} from "use-debounce";
import {SelectSearchRemoteProps} from "@dropDesk/presentation/components/inputs/select_search_remote/select_search";
import {SpinLoading} from "@dropDesk/presentation/components/loadings/spin";
import {Tooltip} from "antd";
import ClipLoading from "@dropDesk/presentation/components/loadings/loading_clip";
import ButtonTopOne from "@dropDesk/presentation/components/inputs/input_with_border/button_top_one";
import {hexToRGBA} from "@dropDesk/utils/helpers/colors";

export default function SelectSearchRemote<T>(props: SelectSearchRemoteProps<T>) {

    const [showLoadMore, setShowLoadMore] = useState(false);
    const suppressNextSearchRef = useRef(false);
    const dropdownOpenRef = useRef(false);
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const borderColor = props.borderColor ?? colors.border;

    const notResultElement =
        <div style={{display: "flex", alignItems: "center", justifyContent: "center", padding: 16}}>
        <span
            style={{
                color: colors.text,
                fontSize: ConstantsStyles.inputFontSize
            }}>Nenhum resultado encontrado na pesquisa.</span>
        </div>;

    const loadingElement =
        <div style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
            <SpinLoading
                color={colors.text}
                size={"default"}
                tip={'Carregando ...'}
            />
        </div>;

    const debounced = useDebouncedCallback(
        async (value, checkIgnore = true) => {
            if (suppressNextSearchRef.current && checkIgnore) {
                suppressNextSearchRef.current = false;
                return;
            }
            if (!dropdownOpenRef.current) {
                return;
            }
            if (value.length === 0 || value.length > ConstantsKeys.defaultNewLimitInputSearch) {
                props.onSearch(value);
            }
        },
        500
    );

    const debouncedOnChange = useDebouncedCallback(
        async (option: any, object?: any) => {
            props.onChange(option ? {label: option.value, value: option.key} : null, object ?? null);
        },
        200
    );

    const onDropdownVisibleChange = (visible: boolean) => {
        dropdownOpenRef.current = visible;
        if (visible && !props.optionsLength && !props.searchValue) {
            debounced('');
        }
    }

    const debouncedSetAtBottom = useDebouncedCallback(async (atBottom: boolean) => {
        if (atBottom && !props.loading && props.hasMore) {
            setShowLoadMore(atBottom);
            if (props.loadMore) {
                await props.loadMore();
                setShowLoadMore(false);
            }
        }
    }, 200);

    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget;
        const threshold = 12;
        const hasScroll = target.scrollHeight > target.clientHeight + 5;
        const atBottom =
            hasScroll &&
            target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
        debouncedSetAtBottom(atBottom);
    };

    const dropdownRenderDefault = (menu: React.ReactNode) => (
        <div>
            {menu}

            {showLoadMore && props.hasMore && (
                <ContainerLoadingMore>
                    <ClipLoading size={15} color={colors.text}/>
                    <TextLoadingMore color={colors.text}>Carregando...</TextLoadingMore>
                </ContainerLoadingMore>
            )}
        </div>
    );

    const dropdownClass = useMemo(() => {
        // classe única por instância (evita conflito se tiver selects com temas diferentes)
        return `dd-select-dropdown-${Math.random().toString(36).slice(2, 9)}`;
    }, []);

    const shadow01 = hexToRGBA(
        colors.brightness === Brightness.light ? SharedColors.black : colors.hint,
        0.1
    );

    const dropdownShadow = `0 3px 6px -4px ${shadow01},
                        0 6px 16px 0 ${shadow01},
                        0 9px 28px 8px ${shadow01}`;

    return (
        <Container
            marginLeft={props.marginLeft ?? 0}
            marginTop={props.marginTop ?? 0}
            marginBottom={props.marginBottom ?? 0}
            useSideLabel={props.useSideLabel ?? false}
            style={{
                maxWidth: props.width ?? '100%',
                width: props.width ?? '100%',
            }}
        >
            <ContentLabel style={{justifyContent: 'space-between'}}>
                {!!props.label &&

                    <Tooltip title={props.textTooltip}>
                        <div>
                            <LabelInput required={props.required} label={props.label}/>
                        </div>
                    </Tooltip>
                }
                <ContentButtons>
                    {props.useButtonRight &&
                        <ButtonTopOne
                            tooltipButtonRight={props.tooltipButtonRight}
                            onClickButtonRight={props.onClickButtonRight}
                            disabledButtonRight={props.disabledButtonRight}
                            nameIconButtonRight={props.nameIconButtonRight}
                            labelButtonRight={props.labelButtonRight}
                        />}
                </ContentButtons>
            </ContentLabel>
            <ContentSelect
                borderColor={borderColor}
                backgroundColor={props.backgroundColor ?? colors.backgroundInput}
                size={props.size ?? 35}
                disabled={props.disabled ?? false}
                accent={colors.accent}
            >
                {props.iconLeftName &&
                    <LeftIcon
                        nameIconLeft={props.iconLeftName}
                        disabled={props.disabled}
                        sizeLeftIcons={14}
                    />}
                <div style={{flex: 1, width: 0, minWidth: 0}}>
                    <SelectDropdownGlobalStyle
                        dropdownClass={dropdownClass}
                        color={colors.text}
                        background={colors.backgroundInput}
                        accent={colors.accent}
                        shadow={dropdownShadow}
                    />
                    <SelectStyled
                        bordered={false}
                        labelInValue
                        dropdownClassName={dropdownClass}
                        allowClear={props.useRemoveFilter ?? false}
                        showSearch={props.search ?? false}
                        disabled={props.disabled}
                        value={!!props.value ? props.value : null}
                        placeholder={props.placeHolder}
                        optionFilterProp="children"
                        onPopupScroll={onScroll}
                        dropdownStyle={{width: props.widthPopup}}
                        dropdownMatchSelectWidth={!props.widthPopup}
                        onDropdownVisibleChange={onDropdownVisibleChange}
                        onChange={(option: any, object: any) => {
                            if (option) {
                                suppressNextSearchRef.current = true;
                            }
                            debouncedOnChange(option, object?.data);
                        }}
                        defaultActiveFirstOption={false}
                        onSearch={(value) => debounced(value)}
                        onClick={props.onClick}
                        onInputKeyDown={(e) => {
                            // bloqueia navegação/seleção via teclado
                            const blocked = ['Enter', 'ArrowUp', 'ArrowDown', 'Tab'];
                            if (blocked.includes(e.key)) {
                                e.preventDefault();
                                e.stopPropagation();
                            }
                        }}
                        onClear={() => {
                            props.onClickRemoveFilter();
                            debounced('', false);

                        }}
                        showAction={['click']}
                        autoClearSearchValue={false}
                        style={
                            {
                                width: props.width ?? '100%',
                                borderColor: borderColor,
                                backgroundColor: 'transparent',
                                borderRadius: 5,
                                fontSize: ConstantsStyles.inputFontSize,
                                color: colors.text
                            }}
                        getPopupContainer={(trigger) => document.body}
                        notFoundContent={props.loading ? loadingElement : notResultElement}
                        filterOption={false}
                        suffixIcon={(
                            <DropDeskIcon
                                icon={ICONS_DROPDESK.downArrow}
                            />
                        )}
                        color={colors.text}
                        placeholdercolor={colors.hint}
                        focuscolor={colors.text}
                        background={colors.backgroundInput}
                        backgroundonprimary={colors.delicateBackground}
                        shadowcolor={colors.brightness === Brightness.light ? SharedColors.black : colors.hint}
                        backgroundmainbutton={colors.accent}
                        dropdownRender={(menu) => {
                            const renderedMenu = props.dropdownRender ? props.dropdownRender(menu) : menu;
                            return dropdownRenderDefault(renderedMenu);
                        }
                        }
                    >
                        {props.options}
                    </SelectStyled>


                </div>
            </ContentSelect>
            {(!!props.messageError || props.messageError === "") && <MessageError messageError={props.messageError}/>}
        </Container>
    );
}
